import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/Functions';

const ConsultationModal = ({ isOpen, onClose, initialService = '' }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // CAPTCHA
  const [captchaImg, setCaptchaImg] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaCode('');
    try {
      const res = await fetch(`${API_URL}/api/public/captcha`);
      const data = await res.json();
      if (data.status === 'success') {
        setCaptchaImg(data.image_base64);
        setCaptchaToken(data.captcha_token);
      }
    } catch (e) {}
    finally { setCaptchaLoading(false); }
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (initialService) {
        const template = `Hello,\n\nI would like to book the "${initialService}" service.\n\nPlease contact me at your earliest convenience to discuss the requirements and the next steps to proceed.\n\nLooking forward to hearing from you.`;
        setFormData({ full_name: '', email: '', phone: '', message: template });
      } else {
        setFormData({ full_name: '', email: '', phone: '', message: '' });
      }
      setSubmitted(false);
      setError(null);
      setFieldErrors({});
      refreshCaptcha();
    }
  }, [isOpen, initialService, refreshCaptcha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    
    if (name === 'full_name') {
      finalValue = value.replace(/[^a-zA-Z\s]/g, '');
    } else if (name === 'phone') {
      const cleaned = value.replace(/(?!^)\+/g, '').replace(/[^\d+]/g, '');
      if (cleaned.length > 14) return;
      finalValue = cleaned;
    }
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (formData.phone && phoneDigits.length < 10) {
      setFieldErrors({ phone: ['Phone number must be at least 10 digits.'] });
      return;
    }
    
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const res = await fetch(`${API_URL}/api/public/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ ...formData, captcha_token: captchaToken, captcha_code: captchaCode })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.errors) {
          setFieldErrors(data.errors);
          console.log('[DEBUG] Validation errors:', data.errors);
          if (data.errors.captcha_code) await refreshCaptcha();
        } else {
          console.log('[DEBUG] Error response:', data.message);
          setError(data.message || 'Something went wrong. Please try again.');
        }
      } else {
        console.log('[DEBUG] Form Data Stored on DB successfully:', data.data);
        console.log('[DEBUG] Email Status:', data.email_status);
        
        const userName = formData.full_name;
        onClose();
        navigate('/thank-you', { state: { name: userName } });
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <style>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.25s ease-out;
        }

        .modal-card {
          background: #ffffff;
          border-radius: 24px;
          width: 90%;
          max-width: 600px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          position: relative;
          border: 1px solid rgba(255, 255, 255, 0.8);
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .modal-header-custom {
          background: linear-gradient(135deg, #061830 0%, #0a2540 100%);
          color: white;
          padding: 28px;
          position: relative;
        }

        .modal-close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(255, 255, 255, 0.15);
          border: none;
          color: white;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.05);
        }

        .modal-body-custom {
          padding: 32px 36px;
        }

        .ct-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ct-field { display: flex; flex-direction: column; gap: 7px; }
        .ct-field.span2 { grid-column: 1 / -1; }
        .ct-label { font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.6px; }
        .ct-label em { color: #ef4444; font-style: normal; }
        .ct-input {
          width: 100%; padding: 13px 16px;
          border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-size: 14.5px; font-family: 'Inter', sans-serif;
          color: #0f172a; background: #f8fafc; outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .ct-input:focus { border-color: #CFA047; background: #fff; box-shadow: 0 0 0 3px rgba(207,160,71,0.12); }
        .ct-input.has-error { border-color: #ef4444; background: #fff5f5; }
        .ct-input::placeholder { color: #94a3b8; }
        .ct-input-textarea { resize: vertical; min-height: 100px; }
        .ct-field-error { font-size: 12px; color: #ef4444; font-weight: 500; display: flex; align-items: center; gap: 5px; }

        /* CAPTCHA */
        .ct-captcha-wrap {
          grid-column: 1 / -1;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 18px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 12px;
        }
        .ct-captcha-wrap.has-error { border-color: #ef4444; }
        .ct-captcha-label { font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.6px; width: 100%; }
        .ct-captcha-label em { color: #ef4444; font-style: normal; }
        .ct-captcha-img-box {
          display: flex; align-items: center; gap: 8px;
          background: #fff; border: 1px dashed #cbd5e1;
          border-radius: 8px; padding: 6px 12px;
        }
        .ct-captcha-img { height: 40px; user-select: none; pointer-events: none; transition: opacity 0.2s; }
        .ct-captcha-refresh {
          background: none; border: none; color: #64748b; cursor: pointer;
          font-size: 15px; padding: 6px 8px; border-radius: 6px; transition: color 0.2s, transform 0.3s;
        }
        .ct-captcha-refresh:hover { color: #0a2540; }
        .ct-captcha-refresh:active { transform: rotate(180deg); }
        .ct-captcha-refresh:disabled { opacity: 0.4; cursor: not-allowed; }
        .ct-captcha-input {
          padding: 10px 14px;
          border: 1.5px solid #e2e8f0; border-radius: 9px;
          font-size: 15px; width: 140px; outline: none; flex: 1;
          background: #fff; letter-spacing: 3px; font-family: monospace;
          transition: border-color 0.2s; box-sizing: border-box;
        }
        .ct-captcha-input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207,160,71,0.12); }
        .ct-captcha-input.has-error { border-color: #ef4444; }
        .ct-captcha-input::placeholder { letter-spacing: 0; font-family: 'Inter', sans-serif; color: #94a3b8; }

        .ct-alert {
          grid-column: 1 / -1;
          background: #fff5f5; border: 1.5px solid #fecaca; border-radius: 10px;
          padding: 14px 18px; color: #dc2626; font-size: 13.5px; font-weight: 500;
          display: flex; align-items: center; gap: 10px;
        }
        .ct-submit-btn {
          grid-column: 1 / -1; width: 100%;
          padding: 16px 32px;
          background: linear-gradient(135deg, #061830 0%, #0a2540 100%);
          color: #fff; border: none; border-radius: 12px;
          font-size: 15px; font-weight: 700; font-family: 'Inter', sans-serif;
          cursor: pointer; transition: all 0.25s;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          box-shadow: 0 6px 20px rgba(6,24,48,0.2);
        }
        .ct-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          background: linear-gradient(135deg, #CFA047 0%, #b8860b 100%);
          box-shadow: 0 10px 28px rgba(207,160,71,0.3);
        }
        .ct-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        /* Success */
        .ct-success { text-align: center; padding: 10px 0 20px; }
        .ct-success-icon {
          width: 80px; height: 80px;
          background: linear-gradient(135deg, #ecfdf5, #d1fae5);
          border: 3px solid #6ee7b7; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 32px; color: #059669; margin: 0 auto 24px;
        }
        .ct-success-title { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0 0 10px; }
        .ct-success-text { font-size: 15px; color: #64748b; line-height: 1.65; margin: 0 0 28px; }
        .ct-success-back {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 28px;
          background: linear-gradient(135deg, #061830, #0a2540);
          color: #fff; border: none; border-radius: 50px;
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: 'Inter', sans-serif; transition: all 0.2s;
        }
        .ct-success-back:hover { background: linear-gradient(135deg, #CFA047, #b8860b); }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @media (max-width: 575px) {
          .ct-form-grid { grid-template-columns: 1fr; }
          .ct-field.span2 { grid-column: auto; }
          .ct-captcha-wrap { grid-column: auto; }
          .ct-alert { grid-column: auto; }
          .ct-submit-btn { grid-column: auto; }
          .modal-body-custom { padding: 24px; }
          .modal-header-custom { padding: 24px; }
        }
      `}</style>

      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-custom">
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <i className="fa-solid fa-xmark"></i>
          </button>
          <h4 className="fw-bold mb-1" style={{ fontSize: '22px', letterSpacing: '-0.5px' }}>Request Free Consultation</h4>
          <p className="small mb-0" style={{ color: 'rgba(255,255,255,0.7)' }}>Provide details below. Our legal experts will contact you shortly.</p>
        </div>

        <div className="modal-body-custom">
            <form onSubmit={handleSubmit}>
              <div className="ct-form-grid">

                <div className="ct-field">
                  <label className="ct-label">Full Name <em>*</em></label>
                  <input
                    type="text" name="full_name"
                    className={`ct-input${fieldErrors.full_name ? ' has-error' : ''}`}
                    placeholder="e.g. Amit Sharma"
                    value={formData.full_name} onChange={handleChange} required
                  />
                  {fieldErrors.full_name && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.full_name[0]}</span>}
                </div>

                <div className="ct-field">
                  <label className="ct-label">Phone Number</label>
                  <input
                    type="tel" name="phone"
                    className={`ct-input${fieldErrors.phone ? ' has-error' : ''}`}
                    placeholder="+91 8608680756"
                    value={formData.phone} onChange={handleChange}
                  />
                  {fieldErrors.phone && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.phone[0]}</span>}
                </div>

                <div className="ct-field span2">
                  <label className="ct-label">Email Address</label>
                  <input
                    type="email" name="email"
                    className={`ct-input${fieldErrors.email ? ' has-error' : ''}`}
                    placeholder="you@example.com"
                    value={formData.email} onChange={handleChange}
                  />
                  {fieldErrors.email && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.email[0]}</span>}
                </div>

                <div className="ct-field span2">
                  <label className="ct-label">Your Message <em>*</em></label>
                  <textarea
                    name="message"
                    className={`ct-input ct-input-textarea${fieldErrors.message ? ' has-error' : ''}`}
                    placeholder="Tell us how we can help you…"
                    value={formData.message} onChange={handleChange} required
                  />
                  {fieldErrors.message && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.message[0]}</span>}
                </div>

                {/* CAPTCHA */}
                <div className={`ct-captcha-wrap${fieldErrors.captcha_code ? ' has-error' : ''}`}>
                  <div className="ct-captcha-label">Security Check <em>*</em></div>
                  <div className="ct-captcha-img-box">
                    {captchaImg
                      ? <img src={captchaImg} alt="captcha" className="ct-captcha-img" style={{ opacity: captchaLoading ? 0.3 : 1 }} />
                      : <div style={{ height: 40, width: 130, background: '#e2e8f0', borderRadius: 4 }} />
                    }
                    <button
                      type="button" className="ct-captcha-refresh"
                      onClick={refreshCaptcha} disabled={captchaLoading}
                      title="Refresh captcha"
                    >
                      <i className={`fa-solid fa-arrows-rotate${captchaLoading ? ' fa-spin' : ''}`}></i>
                    </button>
                  </div>
                  <input
                    type="text"
                    className={`ct-captcha-input${fieldErrors.captcha_code ? ' has-error' : ''}`}
                    placeholder="Enter code"
                    value={captchaCode}
                    onChange={e => { setCaptchaCode(e.target.value); if (fieldErrors.captcha_code) setFieldErrors(p => ({ ...p, captcha_code: null })); }}
                    required autoComplete="off"
                  />
                  {fieldErrors.captcha_code && (
                    <span className="ct-field-error" style={{ width: '100%' }}>
                      <i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.captcha_code[0]}
                    </span>
                  )}
                </div>

                {error && (
                  <div className="ct-alert">
                    <i className="fa-solid fa-triangle-exclamation"></i>{error}
                  </div>
                )}

                <button type="submit" className="ct-submit-btn" disabled={loading}>
                  {loading
                    ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Sending…</>
                    : <>Send Message <i className="fa-solid fa-paper-plane"></i></>
                  }
                </button>

              </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default ConsultationModal;
